import React, { useEffect, useState, useCallback } from "react";
import { Grid, TextField, Button, Typography, Divider, Tooltip, IconButton, Dialog, DialogContent } from "@material-ui/core";
import MuiPhoneNumber from "material-ui-phone-number";
import { useForm, Controller } from "react-hook-form";
import { DateTimePicker } from "@material-ui/pickers";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { useCountry } from "../../../hooks/useCountry";
import clsx from "clsx";
import { useStyles } from "./styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import apiClient from "../../../auth/apiClient";
import CallCenterService from "../../../services/CallCenterService";
import useCancellationToken from "../../../hooks/useCancellationToken";
import {useEnqueueSnackbar} from "../../../hooks/useEnqueueSnackbar";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";

const usePhoneRegex = /^\+1\s?\([0-9]{3}\)\s?[0-9]{3}-[0-9]{4}$/;
const validationSchema = Yup.object()
  .shape({
    validFrom: Yup.date()
      .required("Valid From is required.")
      .test("before", "Valid From must be before Valid To", function(value) {
        return !this.parent.validTo || value < this.parent.validTo;
      }),
    validTo: Yup.date()
      .required("Valid To is required.")
      .test("after", "Valid To must be after Valid From", function(value) {
        return !this.parent.validFrom || value > this.parent.validFrom;
      }),
    phone: Yup.string()
      .transform((curr, orig) => {
        if (orig === "" || curr === "+1" || curr === "+1 ") {
          return null;
        }
        return curr;
      })
      .matches(usePhoneRegex, "Phone number must be of format: (111) 123-4567.")
      .nullable(),
    email: Yup.string()
      .email("Email must be a valid email address.")
      .nullable(),
    name: Yup.string().trim().nullable(),
    roomNumber: Yup.string().trim().nullable(),
    licensePlate: Yup.string().trim()
      .max(8, "License Plate can not be more than 8 characters.")
      .nullable(),
    })
  .test(
    "number-or-email",
    "You must enter either a phone number or an email address.",
    function(value) {
      const hasPhoneNumber = value.phone;
      const hasEmailAddress = value.email;
      return hasPhoneNumber || hasEmailAddress;
    }
  );

const CreateOnePassForm = ({
  onCancel,
  ticketDetails,
  onePassDetails,
  sendOnePass,
  entityID,
  searchOnePass,
  isLPREnabled
}) => {
  const classes = useStyles();
  const { countries } = useCountry();

  const defaultValues = {
    roomNumber: "",
    validFrom: "",
    validTo: moment().format("MM/DD/YYYY h:mm A"),
    name: "",
    entityID,
    email: "",
    phone: "",
    accessGroupID: "",
    ticketID: "",
    overageRateId : "",
    licensePlate : ""
    }

  const [formData, setFormData] = useState(defaultValues);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const callCenterService = new CallCenterService(apiClient);
  const enqueueSnackbar = useEnqueueSnackbar();
  const [imageCapture, setImageCapture] = useState(null);
  const [isLicensePlateImage,setIsLicensePlateImage] = useState(false);
  const { convertUtcDate } = useCurrentFacilityTimezone();

  const getFormattedTime = useCallback((date) => {
    return convertUtcDate(date).format("MM/DD/YYYY h:mm A");
  }, [convertUtcDate]);

  const handleOpenImageViewer = () => {
    executeImageFetch();
  };
  const handleCloseImageViewer = () => setIsImageViewerOpen(false);

  const getValidDate = (date) => {
    if (!date) return null;
    return moment
      .utc(date)
      .local()
      .format("MM/DD/YYYY h:mm A");
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, isDirty },
    reset,
    trigger,
    watch,
    getValues,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: { ...formData },
  });

  const emailValue = watch("email");
  const phoneValue = watch("phone");
  const roomValue = watch("roomNumber");
  const nameValue = watch("name");
  const licensePlateValue=watch("licensePlate");

  useEffect(() => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ticketID: ticketDetails?.ticketID || "",
        validFrom: ticketDetails?.validFrom
        ? getFormattedTime(ticketDetails.validFrom) 
        : getValidDate(ticketDetails?.issuedDate),
        accessGroupID: onePassDetails?.accessGroupId,
        overageRateId: onePassDetails?.overstayRateId,
        validTo: ticketDetails?.validTo
        ? getFormattedTime(ticketDetails.validTo)
        : onePassDetails?.defaultExpirationTime
        ? moment()
            .set({
              hour: moment(onePassDetails?.defaultExpirationTime).hour(),
              minute: moment(onePassDetails?.defaultExpirationTime).minute(),
              second: 0,
              millisecond: 0, 
            })
            .format("MM/DD/YYYY h:mm A")
        : moment().format("MM/DD/YYYY h:mm A"),
        roomNumber: ticketDetails?.roomNumber || "",
        name: ticketDetails?.name || "",
        email: ticketDetails?.email || "",
        phone: ticketDetails?.phone || "",
        licensePlate: ticketDetails?.licensePlate || "",
        lprid : ticketDetails?.lprid || ""
      };

      reset(updatedData);
      return updatedData;
    });
    if(ticketDetails?.ticketID && ticketDetails?.validFrom) {
      setIsSearchEnabled(false);
      setIsUpdateMode(true); 
    } else {
      setIsUpdateMode(false);
    } 
  }, [ticketDetails, onePassDetails, reset]);

  useEffect(() => {
    const isAnyFieldValid =
      [roomValue, nameValue, phoneValue, emailValue,licensePlateValue].some((val) => val?.trim().length >= 3);
    setIsSearchEnabled(isAnyFieldValid);
  }, [roomValue, nameValue, phoneValue, emailValue,licensePlateValue]);

  const permissions = {
    showRoomField: onePassDetails?.showRoomField,
    showNameField: onePassDetails?.showNameField
  };

  const onSubmit = async (data) => {
    if (!isValid) return;
    const isSuccess = await sendOnePass(data, isUpdateMode);
    if(isSuccess) {
      onCancel();
    }
  };

  const handleSearch = async () => {
    const formData = getValues();
    const isSuccess = await searchOnePass(formData);
    if(isSuccess) {
      setIsSearchEnabled(false);
      setIsUpdateMode(true);
    }
  };

  const handleCancelBtn = () => {
    const isUpdateModeEnabled = isUpdateMode;
    if (isUpdateModeEnabled) {
      setIsUpdateMode(false);
      setIsSearchEnabled(false);
    }
    reset(formData);
    onCancel(isUpdateModeEnabled);
  }

  useEffect(() => {
    if(ticketDetails?.lprid){
      setIsLicensePlateImage(true);
    } else {
      setIsLicensePlateImage(false);
    }
  }, [ticketDetails?.lprid]);

  const fetchLPRImage = async () => {
    if(!ticketDetails?.lprid) return;
      try {
        const response = await callCenterService.getLicensePlateCaptureImage(ticketDetails.lprid);
        const fileReader = new FileReader();
        fileReader.readAsDataURL(response.data);
        fileReader.onload = () => {
          setImageCapture(fileReader.result) ;
          setIsImageViewerOpen(true);
        };
      } catch (err) {
        setImageCapture(null) ;
      } 
    };
  
    const {execute: executeImageFetch} = useCancellationToken({
      func: fetchLPRImage,
      errHandleFunc: () => {
        enqueueSnackbar("Failed to load image", {
          variant: "error",
          tag: "FailedToLoadLPRImage",
        });
      },
    });
  
  return (
    <Grid container alignItems="center" justifyContent="center" className={classes.formContainer}>
      <Grid item xs={10} sm={8} className="create-one-pass-form">
        {ticketDetails?.ticketID && (
          <Typography variant="h6" align="center" className="create-one-pass-ticketid">
            Ticket: {ticketDetails?.ticketID?.slice(ticketDetails.ticketID?.length - 12)}
          </Typography>
        )}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}></Grid>
            {permissions.showRoomField && (
              <Grid item xs={12}>
                <Controller
                  name="roomNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="room-input"
                      label="Room"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            )}
            {permissions.showNameField && (
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="name-input"
                      label="Name"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <MuiPhoneNumber
                    {...field}
                    fullWidth
                    id="phone-input"
                    label="Phone"
                    variant="outlined"
                    error={!emailValue && !phoneValue ? true : !!errors.phone}
                    helperText={
                      !emailValue && !phoneValue ? "Phone or Email must be provided" : errors.phone?.message
                    }
                    defaultCountry={"us"}
                    countryCodeEditable={false}
                    regions={["north-america"]}
                    disableAreaCodes
                    onlyCountries={countries.map((country) =>
                      country.alpha2.toLowerCase()
                    )}
                    excludeCountries={["pm", "bm"]}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email-input"
                    label="Email"
                    variant="outlined"
                    fullWidth
                    error={!emailValue && !phoneValue ? true : !!errors.email}
                    helperText={
                      !emailValue && !phoneValue ? "Phone or Email must be provided" : errors.email?.message
                    }
                  />
                )}
              />
            </Grid>
            {isLPREnabled && (
              <Grid item xs={12} container justifyContent="space-between" alignItems="center" >
                <Grid item xs={11}>
                  <Controller
                    name="licensePlate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="license-plate-input"
                        label="License Plate"
                        variant="outlined"
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        inputProps={{
                          minLength: 1,
                          maxLength: 8,
                          "data-testid": "adjusted-licence-plate-text-field",
                        }}
                        helperText={errors.licensePlate?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    onClick={handleOpenImageViewer}
                    aria-label="View context image"
                    edge="end"
                    disabled={!isLicensePlateImage}
                  >
                    <VisibilityIcon
                      fontSize="small"
                      className={clsx(classes.icon, "eyeIcon")}
                      test-id="eyeIcon" />
                  </IconButton>
                </Grid>
              </Grid>)
            }      
            <Divider />
            <Grid item xs={12}>
              <Controller
                name="validFrom"
                control={control}
                rules={{ required: "Valid From is required." }}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    className="create-one-pass-valid-from-date-picker"
                    required
                    label="Valid From"
                    inputVariant="outlined"
                    fullWidth
                    onChange={(date) => {
                      field.onChange(date);
                      trigger("validTo");
                    }}
                    error={!!errors.validFrom}
                    helperText={errors.validFrom?.message}
                    format="MM/DD/YYYY h:mm A"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="validTo"
                control={control}
                rules={{ required: "Valid To is required." }}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    className="create-one-pass-valid-to-date-picker"
                    required
                    label="Valid To"
                    inputVariant="outlined"
                    fullWidth
                    onChange={(date) => {
                      field.onChange(date);
                      trigger("validFrom");
                    }}
                    error={!!errors.validTo}
                    helperText={errors.validTo?.message}
                    format="MM/DD/YYYY h:mm A"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Tooltip title={!isSearchEnabled ? "Please enter information to search on" : ""}>
                  <span>
                    <Button
                      type="button"
                      name="search"
                      variant="outlined"
                      color="primary"
                      className="search-button"
                      disabled={!isSearchEnabled || isUpdateMode}
                      onClick={handleSearch}
                    >
                      Search
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item>
                <Grid container spacing={1}>
                  <Grid item>
                    <Button
                      type="submit"
                      name="create"
                      variant="contained"
                      color="primary"
                      disabled={!isValid || isSubmitting || !isDirty}
                      className="create-one-pass-send-button"
                    >
                      {isUpdateMode? "Update" : "Create"}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleCancelBtn}
                      className={clsx("create-one-pass-cancel-button", classes.createOnePassCancelButton)}
                    >
                      {isUpdateMode? "Cancel" : "Back"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
      <Dialog open={isImageViewerOpen} onClose={handleCloseImageViewer} maxWidth="md" fullWidth>
        <DialogContent  className={clsx("Dialog-content", classes.dialogContent)} >
          <img
            src={imageCapture}
            alt="Context"
            className={clsx("image-content", classes.image)}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default CreateOnePassForm;
